import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { trigger, state, style, transition, animate, AnimationEvent } from '@angular/animations';

@Component({
	selector: 'app-cycling-loading-messages',
	imports: [CommonModule],
	templateUrl: './cycling-loading-messages.component.html',
	styleUrl: './cycling-loading-messages.component.scss',
	standalone: true,
	animations: [
		trigger('fadeInOut', [
			state('visible', style({
				opacity: 1
			})),
			state('hidden', style({
				opacity: 0
			})),
			transition('visible => hidden', [
				animate('300ms ease-out')
			]),
			transition('hidden => visible', [
				animate('300ms ease-in')
			])
		])
	]
})
export class CyclingLoadingMessagesComponent implements OnInit, OnDestroy {
	// Default messages to use
	private readonly defaultMessages: string[] = [
		'Ladataan...',
		'Odota vielä hetki...',

		'Kohta valmista...',
		'Käsitellään...',
		'Kestää vielä tovi...',
	];

	// Input for loading messages array - if not provided, uses default messages
	@Input() loadingMessages: string[] = [];

	// Input to include default messages after custom messages
	@Input() includeDefaultMessages = false;

	// Input for the cycle interval in seconds (default: 8)
	@Input() cycleIntervalSeconds = 8;

	// Current message to display
	currentMessage = '';

	// Animation state for fade effect
	animationState: 'visible' | 'hidden' = 'visible';

	// Timer interval reference
	private intervalId?: number;

	// Index for cycling through messages
	private currentMessageIndex = 0;

	// Messages to actually cycle through
	private messagesToCycle: string[] = [];

	ngOnInit(): void {
		// Build the messages array
		if (this.loadingMessages && this.loadingMessages.length > 0) {
			this.messagesToCycle = [...this.loadingMessages];

			// Add default messages if requested
			if (this.includeDefaultMessages) {
				this.messagesToCycle = [
					...this.messagesToCycle,
					...this.defaultMessages,
				];
			}
		} else {
			// No custom messages provided, use defaults
			this.messagesToCycle = [...this.defaultMessages];
		}

		// Validate that we have at least one message
		if (this.messagesToCycle.length === 0) {
			this.messagesToCycle = ['Ladataan...'];
		}

		// Set initial message (first message in the array)
		this.currentMessage = this.messagesToCycle[0];

		// Only start cycling if we have more than one message
		if (this.messagesToCycle.length > 1) {
			// Start cycling through messages after the specified interval
			this.intervalId = window.setInterval(() => {
				this.cycleToNextMessage();
			}, this.cycleIntervalSeconds * 1000);
		}
	}

	ngOnDestroy(): void {
		// Clean up interval to prevent memory leaks
		if (this.intervalId) {
			clearInterval(this.intervalId);
		}
	}

	private cycleToNextMessage(): void {
		// Start fade out animation
		this.animationState = 'hidden';
	}

	// Animation callback to handle message change after fade out
	onAnimationDone(event: AnimationEvent): void {
		// Only change message after fade out completes
		if (event.toState === 'hidden') {
			// Move to next message
			this.currentMessageIndex =
				(this.currentMessageIndex + 1) % this.messagesToCycle.length;
			this.currentMessage =
				this.messagesToCycle[this.currentMessageIndex];

			// Start fade in animation
			this.animationState = 'visible';
		}
	}
}
